/**
 * Native Bills Context — Firestore is source of truth. No Xero.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';
import { collection, getDocs, getFirestore, query, orderBy, limit } from 'firebase/firestore';
import { authFetch } from '../../../utils/cloudFunctionsAuth';
import { Bill, BillFilters, BillStatus } from '../../../types/accounting';

const CF_BASE = 'https://us-central1-web-construct-app.cloudfunctions.net';

interface BillsContextState {
  bills: Bill[];
  /** ALL bills (unfiltered) — for filter-independent KPI summary cards. */
  allBills: Bill[];
  billsLoading: boolean;
  billsError: string | null;
  billFilters: BillFilters;
  createBillLoading: boolean;
  updateBillLoading: boolean;
  billDetailLoading: boolean;
  billActionLoading: string | null;
  connectionStatus: { connected: boolean };
  paymentsLoading: string | null;
  recordPaymentLoading: boolean;
  deletePaymentLoading: string | null;
}

interface BillsContextActions {
  setBillFilters: (filters: Partial<BillFilters>) => void;
  fetchBills: (filters?: Partial<BillFilters>) => Promise<void>;
  createBill: (payload: {
    contactId?: string;
    contactName: string;
    contactEmail?: string;
    reference?: string;
    date?: string;
    dueDate?: string;
    currencyCode?: string;
    lineItems: Array<{ description: string; quantity: number; unitAmount: number; taxType?: 'GST' | 'GST_FREE' | 'NONE'; accountCode?: string }>;
  }) => Promise<{ bill: Bill }>;
  updateBill: (payload: {
    billId: string;
    contactId?: string;
    contactName?: string;
    contactEmail?: string;
    reference?: string;
    date?: string;
    dueDate?: string;
    lineItems?: Array<{ description: string; quantity: number; unitAmount: number; taxType?: 'GST' | 'GST_FREE' | 'NONE'; accountCode?: string }>;
  }) => Promise<{ bill: Bill }>;
  fetchBillDetail: (billId: string) => Promise<Bill | null>;
  voidBill: (billId: string) => Promise<void>;
  deleteDraftBill: (billId: string) => Promise<void>;
  approveBill: (billId: string) => Promise<void>;
  fetchPayments: (billId: string) => Promise<any[]>;
  recordPayment: (payload: { billId: string; method: string; amount: number; date?: string; reference?: string }) => Promise<void>;
  deletePayment: (paymentId: string, billId: string) => Promise<void>;
}

type BillsContextType = BillsContextState & BillsContextActions;

const defaultBillFilters: BillFilters = {
  status: 'APPROVED',
};

const BillsContext = createContext<BillsContextType | null>(null);

export function useBills(): BillsContextType {
  const ctx = useContext(BillsContext);
  if (!ctx) throw new Error('useBills must be used within BillsProvider');
  return ctx;
}

export function BillsProvider({ children }: { children: ReactNode }) {
  const [bills, setBills] = useState<Bill[]>([]);
  const [allBills, setAllBills] = useState<Bill[]>([]);
  const [billsLoading, setBillsLoading] = useState(true);
  const [billsError, setBillsError] = useState<string | null>(null);
  const [billFilters, setBillFiltersState] = useState<BillFilters>(defaultBillFilters);
  const [createBillLoading, setCreateBillLoading] = useState(false);
  const [updateBillLoading, setUpdateBillLoading] = useState(false);
  const [billDetailLoading, setBillDetailLoading] = useState(false);
  const [billActionLoading, setBillActionLoading] = useState<string | null>(null);
  const [paymentsLoading, setPaymentsLoading] = useState<string | null>(null);
  const [recordPaymentLoading, setRecordPaymentLoading] = useState(false);
  const [deletePaymentLoading, setDeletePaymentLoading] = useState<string | null>(null);

  const setBillFilters = useCallback((filters: Partial<BillFilters>) => {
    setBillFiltersState(prev => ({ ...prev, ...filters }));
  }, []);

  const fetchBills = useCallback(async (overrides?: Partial<BillFilters>) => {
    const filters = { ...billFilters, ...overrides };
    setBillsLoading(true);
    setBillsError(null);
    try {
      const db = getFirestore();
      const q = query(
        collection(db, 'bills'),
        orderBy('dueDate', 'desc'),
        limit(2000)
      );
      const snap = await getDocs(q);
      const allList: Bill[] = snap.docs.map(d => ({ id: d.id, ...d.data() } as Bill));
      setAllBills(allList); // filter-independent source for the KPI cards (audit: high)
      let list: Bill[] = allList;

      if (filters.status) {
        const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
        list = list.filter(b => {
          const effectiveStatus = b.status === 'SENT' ? 'APPROVED' : b.status;
          return statuses.includes(effectiveStatus);
        });
      }
      if (filters.contactId) list = list.filter(b => b.contactId === filters.contactId);
      if (filters.dateFrom) list = list.filter(b => b.dueDate >= filters.dateFrom!);
      if (filters.dateTo) list = list.filter(b => b.dueDate <= filters.dateTo!);
      if (filters.search) {
        const s = filters.search.toLowerCase();
        list = list.filter(b => {
          if ((b.billNumber || '').toLowerCase().includes(s)) return true;
          if ((b.contactName || '').toLowerCase().includes(s)) return true;
          if ((b.reference || '').toLowerCase().includes(s)) return true;
          // Line-item description search (added 2026-05). Catches
          // bills by what was purchased ("timber", "site fees") when
          // the user can't remember the bill number.
          const lines = Array.isArray(b.lineItems) ? b.lineItems : [];
          for (const li of lines) {
            if ((li?.description || '').toLowerCase().includes(s)) return true;
          }
          return false;
        });
      }

      setBills(list);
    } catch (e: any) {
      setBillsError(e?.message || 'Failed to fetch bills');
      setBills([]);
    } finally {
      setBillsLoading(false);
    }
  }, [billFilters]);

  useEffect(() => {
    fetchBills();
  }, [billFilters.status, billFilters.contactId, billFilters.dateFrom, billFilters.dateTo, billFilters.search]);

  const createBill = useCallback(async (payload: any) => {
    setCreateBillLoading(true);
    try {
      const res = await authFetch(`${CF_BASE}/createBillHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Create failed');
      await fetchBills();
      return { bill: data.bill as Bill };
    } finally {
      setCreateBillLoading(false);
    }
  }, [fetchBills]);

  const updateBill = useCallback(async (payload: any) => {
    setUpdateBillLoading(true);
    try {
      const res = await authFetch(`${CF_BASE}/updateBillHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Update failed');
      await fetchBills();
      return { bill: data.bill as Bill };
    } finally {
      setUpdateBillLoading(false);
    }
  }, [fetchBills]);

  const fetchBillDetail = useCallback(async (billId: string): Promise<Bill | null> => {
    setBillDetailLoading(true);
    try {
      const res = await authFetch(`${CF_BASE}/getBillHttp?billId=${encodeURIComponent(billId)}`, { method: 'GET' });
      const data = await res.json();
      if (!data.ok || !data.bill) return null;
      return { id: data.bill.id, ...data.bill } as Bill;
    } catch {
      return null;
    } finally {
      setBillDetailLoading(false);
    }
  }, []);

  const voidBill = useCallback(async (billId: string) => {
    setBillActionLoading(billId);
    try {
      const res = await authFetch(`${CF_BASE}/voidBillHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ billId }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Void failed');
      await fetchBills();
    } finally {
      setBillActionLoading(null);
    }
  }, [fetchBills]);

  const deleteDraftBill = useCallback(async (billId: string) => {
    setBillActionLoading(billId);
    try {
      const res = await authFetch(`${CF_BASE}/deleteBillHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ billId }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Delete failed');
      await fetchBills();
    } finally {
      setBillActionLoading(null);
    }
  }, [fetchBills]);

  const approveBill = useCallback(async (billId: string) => {
    setBillActionLoading(billId);
    try {
      const res = await authFetch(`${CF_BASE}/approveBillHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ billId }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Approve failed');
      await fetchBills();
    } finally {
      setBillActionLoading(null);
    }
  }, [fetchBills]);

  const fetchPayments = useCallback(async (billId: string): Promise<any[]> => {
    setPaymentsLoading(billId);
    try {
      const res = await authFetch(`${CF_BASE}/getPaymentsHttp?documentType=bill&documentId=${encodeURIComponent(billId)}`, { method: 'GET' });
      const data = await res.json();
      if (!data.ok) return [];
      return data.payments || [];
    } catch {
      return [];
    } finally {
      setPaymentsLoading(prev => (prev === billId ? null : prev));
    }
  }, []);

  const recordPayment = useCallback(async (payload: { billId: string; method: string; amount: number; date?: string; reference?: string }) => {
    setRecordPaymentLoading(true);
    try {
      const res = await authFetch(`${CF_BASE}/recordPaymentHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentType: 'bill',
          documentId: payload.billId,
          amount: payload.amount,
          date: payload.date,
          method: payload.method || 'OTHER',
          reference: payload.reference,
        }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Record payment failed');
      await fetchBills();
    } finally {
      setRecordPaymentLoading(false);
    }
  }, [fetchBills]);

  const deletePayment = useCallback(async (paymentId: string, _billId: string) => {
    setDeletePaymentLoading(paymentId);
    try {
      const res = await authFetch(`${CF_BASE}/deletePaymentHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentId }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Delete payment failed');
      await fetchBills();
    } finally {
      setDeletePaymentLoading(null);
    }
  }, [fetchBills]);

  const value: BillsContextType = useMemo(() => ({
    bills,
    allBills,
    billsLoading,
    billsError,
    billFilters,
    createBillLoading,
    updateBillLoading,
    billDetailLoading,
    billActionLoading,
    connectionStatus: { connected: true },
    setBillFilters,
    fetchBills,
    createBill,
    updateBill,
    fetchBillDetail,
    voidBill,
    deleteDraftBill,
    approveBill,
    fetchPayments,
    recordPayment,
    deletePayment,
    paymentsLoading,
    recordPaymentLoading,
    deletePaymentLoading,
  }), [
    bills,
    allBills,
    billsLoading,
    billsError,
    billFilters,
    createBillLoading,
    updateBillLoading,
    billDetailLoading,
    billActionLoading,
    setBillFilters,
    fetchBills,
    createBill,
    updateBill,
    fetchBillDetail,
    voidBill,
    deleteDraftBill,
    approveBill,
    fetchPayments,
    recordPayment,
    deletePayment,
    paymentsLoading,
    recordPaymentLoading,
    deletePaymentLoading,
  ]);

  return <BillsContext.Provider value={value}>{children}</BillsContext.Provider>;
}

export default BillsContext;
